import React from 'react'

import { flatten } from 'ramda'

import { PagedResponse, SerializableFilterGroup } from 'api'
import { DateRange } from 'components/DateRangePicker'
import { transformFiltersToBackend } from 'utils/deserializeFilters'
import { FilterPage } from 'utils/filters'

import { useDebounce } from './useDebounce'
import { useAppSelector } from './useRedux'

const CURRENT_USER_MARKER = '<userId>'

export const getCurrentState = <T>(
  pagedData: PagedResponse<T>[] | undefined,
  error: any,
  size: number | undefined
) => {
  const data = flatten(pagedData?.map(pagedData => pagedData.data) ?? [])
  const isLoadingInitialData = !pagedData && !error
  const hasLoadedInitialData = !!(pagedData && !error)
  const isLoadingMore = !!(
    isLoadingInitialData ||
    (pagedData && typeof pagedData[(size ?? 1) - 1] === 'undefined')
  )
  const isEmpty = pagedData?.[0]?.totalCount === 0
  const isReachingEnd = !!(isEmpty || (pagedData && !pagedData[pagedData.length - 1].nextPagePath))

  return { data, isLoadingInitialData, hasLoadedInitialData, isLoadingMore, isReachingEnd }
}

export const setResultSetSize = <T>(
  setResultSize: (value: React.SetStateAction<number | undefined>) => void,
  pagedData: PagedResponse<T>[] | undefined,
  resultSize: number | undefined
) => {
  if (pagedData?.[0]?.totalCount !== undefined && pagedData?.[0]?.totalCount !== resultSize)
    setResultSize(pagedData[0].totalCount)
}

const substituteCurrentUser = (
  filterGroup: SerializableFilterGroup,
  currentUserId: number | undefined
): SerializableFilterGroup => {
  const substitute = (value: string): string =>
    value === CURRENT_USER_MARKER ? currentUserId?.toString() || '0' : value

  const updatedAccountOwnerFilter = filterGroup.accountOwnerFilter && {
    ...filterGroup.accountOwnerFilter,
    values: filterGroup.accountOwnerFilter?.values?.map(substitute)
  }

  const updatedAttributesFilters = filterGroup.attributeFilters?.map(attributeFilter => ({
    ...attributeFilter,
    values: attributeFilter.values?.map(substitute)
  }))

  return {
    ...filterGroup,
    accountOwnerFilter: updatedAccountOwnerFilter,
    attributeFilters: updatedAttributesFilters
  }
}

export const useTransformedFilters = (
  filterPage: FilterPage,
  currentUserId: number | undefined,
  customDateRange?: DateRange
): {
  searchQuery: string | undefined
  dateRange: DateRange | undefined
  serializableFilterGroups: SerializableFilterGroup[]
} => {
  const { searchQuery, dateRange, filterGroupsByPage } = useAppSelector(({ filters }) => filters)
  const filterGroups = filterGroupsByPage[filterPage] || [[]]

  const serializableFilterGroups = transformFiltersToBackend(filterGroups).map(filterGroup =>
    substituteCurrentUser(filterGroup, currentUserId)
  )

  return {
    serializableFilterGroups,
    searchQuery: useDebounce(searchQuery, 200),
    dateRange: customDateRange || dateRange
  }
}

export interface PagedResponseData<T> {
  data: T[]
  resultSize?: number
  error: any
  mutate: any
  size?: number
  setSize: any
  isLoadingMore: boolean
  isLoadingInitialData: boolean
  hasLoadedInitialData: boolean
  isReachingEnd: boolean
}
